import { useEffect, useState } from 'react';
import { Head } from '@inertiajs/react';
import { Typography, Dialog, DialogTitle, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MainLayout from '../../components/MainLayout';
import { SecondaryButton, PrimaryButton } from '../../components';
import CurrentBillsCard, { type CurrentCycle } from '../../components/CurrentBillsCard';
import type { ExpectedBill } from '../../components/ExpectedBillsCard';
import CustomDatePicker from '../../components/CustomDatePicker';
import emptyStateImage from '../../assets/emptytour.svg';

export type UtilBillPaymentStatus = 'calculated' | 'roommate_notified' | 'paid';

export interface UtilBillPayment {
    id: string;
    date_paid?: string | null;
    roommate_id: string;
    roommate_name: string;
    roommate_cost: number;
    status: UtilBillPaymentStatus;
}

export interface UtilityBill {
    id: string;
    due_date: string;
    total_cost: number;
    is_duplicate?: boolean;
    utility_type: string;
    icon: string;
    util_email_id?: string | null;
    payments: UtilBillPayment[];
}

export interface BillsByMonthItem {
    month_label: string;
    bills: UtilityBill[];
}

/** @deprecated Use BillsByMonthItem instead */
export type PastBillsByMonthItem = BillsByMonthItem;

interface UtilBillsProps {
    household_id: string;
    billing_cycles?: CurrentCycle[];
    current_user_roommate_id?: string | null;
    bill_payer_venmo_handle?: string | null;
    is_customer?: boolean;
    current_roommate_is_admin?: boolean;
    expected_bills?: ExpectedBill[];
}

export function formatMonthYear(dateStr: string): string {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export default function UtilBills(props: UtilBillsProps) {
    const currentUserRoommateId = props.current_user_roommate_id ?? 'current';
    const billPayerVenmoHandle = props.bill_payer_venmo_handle;

    const [billingCycles, setBillingCycles] = useState<CurrentCycle[]>(props.billing_cycles ?? []);
    const [expectedBills, setExpectedBills] = useState<ExpectedBill[]>(props.expected_bills ?? []);

    useEffect(() => {
        setBillingCycles(props.billing_cycles ?? []);
    }, [props.billing_cycles]);

    useEffect(() => {
        setExpectedBills(props.expected_bills ?? []);
    }, [props.expected_bills]);

    const [leaveHouseholdOpen, setLeaveHouseholdOpen] = useState(false);
    const [moveOutDate, setMoveOutDate] = useState<Date | null>(null);

    const handleOpenLeaveHousehold = () => {
        setMoveOutDate(null);
        setLeaveHouseholdOpen(true);
    };
    const handleCloseLeaveHousehold = () => {
        setLeaveHouseholdOpen(false);
        setMoveOutDate(null);
    };
    const handleConfirmLeaveHousehold = () => {
        // TODO: submit leave household to backend
        handleCloseLeaveHousehold();
    };

    const handleCycleChange = (updated: CurrentCycle) => {
        setBillingCycles((prev) => prev.map((cycle) => (cycle.id === updated.id ? updated : cycle)));
    };

    const hasNoBills = billingCycles.length === 0
        || billingCycles.every((cycle) => cycle.bills.length === 0 && cycle.unmatched_bills.length === 0);

    return (
        <>
            <Head title="Bills - HeyyProperty" />
            <MainLayout
                headerButton={
                    <SecondaryButton onClick={handleOpenLeaveHousehold}>
                        Leave Household
                    </SecondaryButton>
                }
                utilDashboard
                householdId={props.household_id}
            >
                {hasNoBills ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" textAlign="left" border="1px solid grey" p={2} borderRadius="16px">
                        <Typography fontWeight="bold" mb={2}>
                            No bills received yet
                        </Typography>
                        <Typography mb={4} color="text.secondary">
                            Forward your utility bills to your household email and we'll split them for you.
                        </Typography>
                        <img src={emptyStateImage} alt="No bills" style={{ maxWidth: '300px', height: 'auto' }} />
                    </Box>
                ) : (
                    billingCycles.map((cycle) => (
                        <CurrentBillsCard
                            key={cycle.id}
                            currentCycle={cycle}
                            householdId={props.household_id}
                            currentUserRoommateId={currentUserRoommateId}
                            expectedBills={expectedBills}
                            billPayerVenmoHandle={billPayerVenmoHandle}
                            isCustomer={props.is_customer}
                            currentRoommateIsAdmin={props.current_roommate_is_admin}
                            onCurrentCycleChange={handleCycleChange}
                        />
                    ))
                )}
            </MainLayout>

            {/* Leave Household Dialog */}
            <Dialog
                open={leaveHouseholdOpen}
                onClose={handleCloseLeaveHousehold}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Leave Household
                    </Typography>
                    <IconButton onClick={handleCloseLeaveHousehold} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        When are you moving out?
                    </Typography>
                    <CustomDatePicker
                        label="Move-out date"
                        value={moveOutDate}
                        onChange={setMoveOutDate}
                        minDate={null}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                        <SecondaryButton fullWidth onClick={handleCloseLeaveHousehold}>
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton
                            fullWidth
                            onClick={handleConfirmLeaveHousehold}
                            disabled={!moveOutDate}
                        >
                            Leave
                        </PrimaryButton>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
